import { useState, useEffect, useRef } from "react";
import "./Mapping.scss";
import {
  getWhatsappMappingsApi,
  createWhatsappMappingApi,
  updateWhatsappMappingApi,
  deleteWhatsappMappingApi,
  getWhatsappGroupsApi,
  getWhatsappUsersApi,
} from "../../../../../api/api";

const AVATAR_COLORS = [
  "#0062FE",
  "#7C3AED",
  "#059669",
  "#D97706",
  "#DC2626",
  "#0891B2",
];

function toUiMapping(m) {
  return {
    id: m.id,
    whatsappIdentity: m.whatsappIdentity,
    mappingType: m.mappingType,
    matrixUserId: m.matrixUserId,
    number: m.whatsappIdentity,
    displayName:
      m.mappingType === "DIRECT"
        ? m.matrixUserId
        : m.mappingName || m.whatsappIdentity,
    room: m.appRoomId || "",
    roomName: m.mappingName || "",
    mappedBy: m.mappedBy || "Admin",
    status: m.status?.toLowerCase() === "active" ? "active" : "paused",
    lastRouted: m.lastRouted || "Never",
    senderRule: m.senderRule || "All",
    mediaHandling: m.mediaHandling || "Download and post",
    createdAt: m.createdAt,
  };
}

function toApiPayload(form) {
  return {
    whatsappIdentity: form.whatsappIdentity,
    mappingType: form.mappingType,
    appRoomId: form.mappingType === "GROUP" ? form.groupId : null,
    matrixUserId: form.mappingType === "DIRECT" ? form.matrixUserId : null,
  };
}

function StatusBadge({ status }) {
  const labels = { active: "Active", paused: "Paused", inactive: "Inactive" };
  const icons = { active: "●", paused: "⏸", inactive: "○" };
  return (
    <span className={`badge badge--${status}`}>
      {icons[status]} {labels[status] || status}
    </span>
  );
}

function Toggle({ on, onChange }) {
  return (
    <button
      className={`toggle toggle--${on ? "on" : "off"}`}
      onClick={() => onChange(!on)}
      title={on ? "Pause" : "Activate"}
    >
      <span className={`toggle__thumb toggle__thumb--${on ? "on" : "off"}`} />
    </button>
  );
}

function KpiCard({ label, value, trend, positive = true, icon }) {
  return (
    <div className="kpi-card">
      <div className="kpi-card__header">
        <span className="kpi-card__label">{label}</span>
        <span className="kpi-card__icon">{icon}</span>
      </div>
      <div className="kpi-card__value">{value}</div>
      <span
        className={`kpi-card__trend${positive ? "" : " kpi-card__trend--negative"}`}
      >
        {trend}
      </span>
    </div>
  );
}

function AddMappingDrawer({
  onClose,
  onSave,
  editData,
  saving,
  groups,
  users,
}) {
  const [form, setForm] = useState(
    editData || {
      whatsappIdentity: "",
      mappingType: "GROUP",
      groupId: "",
      matrixUserId: "",
      senderRule: "All",
      mediaHandling: "Download and post",
      status: "active",
    },
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div
      className="drawer-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="drawer">
        <div className="drawer__header">
          <span className="drawer__title">
            {editData ? "Edit Mapping" : "Add WhatsApp Mapping"}
          </span>
          <button className="btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="drawer__body">
          <div className="form-section-label">WhatsApp Details</div>
          <div className="form-group">
            <label className="form-label">WhatsApp Number *</label>
            <input
              className="form-input"
              placeholder="919999999999"
              value={form.whatsappIdentity}
              onChange={(e) => set("whatsappIdentity", e.target.value)}
            />
          </div>
          <div className="form-divider" />
          <div className="form-section-label">Routing Configuration</div>
          <div className="form-group">
            <label className="form-label">Mapping Type</label>
            <select
              className="form-input form-input--select"
              value={form.mappingType}
              onChange={(e) => set("mappingType", e.target.value)}
            >
              <option value="GROUP">GROUP</option>
              <option value="DIRECT">DIRECT</option>
            </select>
          </div>
          {form.mappingType === "GROUP" && (
            <div className="form-group">
              <label className="form-label">Target Group *</label>
              <select
                className="form-input form-input--select"
                value={form.groupId}
                onChange={(e) => set("groupId", e.target.value)}
              >
                <option value="">Select a group…</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.roomName}
                  </option>
                ))}
              </select>
            </div>
          )}
          {form.mappingType === "DIRECT" && (
            <div className="form-group">
              <label className="form-label">Matrix User *</label>
              <select
                className="form-input form-input--select"
                value={form.matrixUserId}
                onChange={(e) => set("matrixUserId", e.target.value)}
              >
                <option value="">Select a user…</option>
                {users.map((u) => (
                  <option key={u.matrixUserId} value={u.matrixUserId}>
                    {u.appUser?.fullName} ({u.matrixUserId})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Allowed Senders</label>
            <select
              className="form-input form-input--select"
              value={form.senderRule}
              onChange={(e) => set("senderRule", e.target.value)}
            >
              <option>All</option>
              <option>Whitelist</option>
              <option>Blacklist</option>
            </select>
          </div>
          <div className="form-divider" />
          <div className="form-section-label">Media Policy</div>
          <div className="form-group">
            <label className="form-label">Media Handling</label>
            <select
              className="form-input form-input--select"
              value={form.mediaHandling}
              onChange={(e) => set("mediaHandling", e.target.value)}
            >
              <option>Download and post</option>
              <option>Log only</option>
              <option>Reject media</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Unknown Sender Rule</label>
            <select className="form-input form-input--select">
              <option>Post as external contact</option>
              <option>Reject silently</option>
              <option>Log only</option>
            </select>
          </div>
          <div className="form-divider" />
          <div className="form-section-label">Status</div>
          <div className="form-toggle-row">
            <Toggle
              on={form.status === "active"}
              onChange={(v) => set("status", v ? "active" : "paused")}
            />
            <span>{form.status === "active" ? "Active" : "Paused"}</span>
          </div>
        </div>
        <div className="drawer__footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={() => onSave(form)}
            disabled={saving}
          >
            {saving ? "Saving…" : editData ? "Save Changes" : "Add Mapping"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── RowMenu: uses position:fixed to escape any overflow:hidden parent ────────
function RowMenu({ onEdit, onLogs, onToggle, onDelete, status }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const handleToggleMenu = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.right - 175,
      });
    }
    setOpen((o) => !o);
  };

  // Close on any outside click
  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [open]);

  return (
    <div className="row-menu">
      <button ref={btnRef} className="btn-icon" onClick={handleToggleMenu}>
        ⋯
      </button>
      {open && (
        <div
          className="drop-menu"
          style={{ position: "fixed", top: menuPos.top, left: menuPos.left }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="drop-menu__item"
            onClick={() => {
              onEdit();
              setOpen(false);
            }}
          >
            ✏️ Edit Mapping
          </div>

          <div
            className="drop-menu__item"
            onClick={() => {
              onToggle();
              setOpen(false);
            }}
          >
            {status === "active" ? "⏸ Pause Routing" : "▶ Resume Routing"}
          </div>
          <div className="drop-menu__divider" />
          <div
            className="drop-menu__item drop-menu__item--danger"
            onClick={() => {
              onDelete();
              setOpen(false);
            }}
          >
            🗑 Remove Mapping
          </div>
        </div>
      )}
    </div>
  );
}

export default function Mapping() {
  const [mappings, setMappings] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showDrawer, setShowDrawer] = useState(false);
  const [editData, setEditData] = useState(null);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    fetchMappings();
    fetchGroups();
    fetchUsers();
  }, []);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const res = await getWhatsappMappingsApi();
      const data = res.data;
      if (data.success && Array.isArray(data.mappings)) {
        setMappings(data.mappings.map(toUiMapping));
      }
    } catch (err) {
      showToast("❌ Failed to load mappings");
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await getWhatsappGroupsApi();
      if (res.data.success) setGroups(res.data.groups);
    } catch (err) {
      showToast("❌ Failed to load groups");
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await getWhatsappUsersApi();
      if (res.data.success) setUsers(res.data.users);
    } catch (err) {
      showToast("❌ Failed to load users");
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  const getGroupName = (roomId) => {
    const g = groups.find((g) => g.id === roomId);
    return g ? g.roomName : roomId;
  };

  const filtered = mappings.filter((m) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (m.number || "").toLowerCase().includes(q) ||
      (m.roomName || getGroupName(m.room) || "").toLowerCase().includes(q);
    const matchStatus = statusFilter === "all" || m.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editData) {
        const res = await updateWhatsappMappingApi(
          editData.id,
          toApiPayload(form),
        );
        if (res.data.success) {
          setMappings((ms) =>
            ms.map((m) =>
              m.id === editData.id
                ? {
                    ...m,
                    whatsappIdentity: form.whatsappIdentity,
                    number: form.whatsappIdentity,
                    mappingType: form.mappingType,
                    room: form.mappingType === "GROUP" ? form.groupId : "",
                    matrixUserId:
                      form.mappingType === "DIRECT" ? form.matrixUserId : null,
                    roomName:
                      form.mappingType === "GROUP"
                        ? getGroupName(form.groupId)
                        : "",
                  }
                : m,
            ),
          );
          showToast("✅ Mapping updated");
        }
      } else {
        const res = await createWhatsappMappingApi(toApiPayload(form));
        if (res.data.success) {
          setMappings((ms) => [
            ...ms,
            toUiMapping({
              ...res.data.mapping,
              status: form.status,
              senderRule: form.senderRule,
              mediaHandling: form.mediaHandling,
              mappedBy: "Admin",
              lastRouted: "Never",
            }),
          ]);
          showToast("✅ Mapping created");
        }
      }
      setShowDrawer(false);
      setEditData(null);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (editData ? "Failed to update mapping" : "Failed to create mapping");
      showToast(`❌ ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    const mapping = mappings.find((m) => m.id === id);
    const newStatus = mapping.status === "active" ? "paused" : "active";
    setMappings((ms) =>
      ms.map((m) => (m.id === id ? { ...m, status: newStatus } : m)),
    );
    try {
      await updateWhatsappMappingApi(id, { status: newStatus });
      showToast(`Status set to ${newStatus}`);
    } catch (err) {
      setMappings((ms) =>
        ms.map((m) => (m.id === id ? { ...m, status: mapping.status } : m)),
      );
      showToast("❌ Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    const prev = mappings;
    setMappings((ms) => ms.filter((m) => m.id !== id));
    try {
      await deleteWhatsappMappingApi(id);
      showToast("🗑 Mapping removed");
    } catch (err) {
      setMappings(prev);
      showToast("❌ Failed to delete mapping");
    }
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="mapping-page">
      <div className="mapping-header">
        <div className="mapping-header__left">
          <h1 className="mapping-header__title">WhatsApp Mapping</h1>
          <p className="mapping-header__desc">
            Map official WhatsApp Business numbers to internal Murasal rooms and
            configure routing rules.
          </p>
        </div>
        <div className="mapping-header__actions">
          <button
            className="btn-primary"
            onClick={() => showToast("📤 Export functionality coming soon")}
          >
            <span>📤</span> Export
          </button>
          <button
            className="btn-primary"
            onClick={() => {
              setEditData(null);
              setShowDrawer(true);
            }}
          >
            <span>＋</span> Add Mapping
          </button>
        </div>
      </div>

      <div className="mapping-content">
        <div className="kpi-row">
          <KpiCard
            label="Total Mappings"
            value={mappings.length}
            trend="↑ +2 this week"
            icon="🔗"
          />
          <KpiCard
            label="Active Mappings"
            value={mappings.filter((m) => m.status === "active").length}
            trend="All routing live"
            icon="✅"
          />
          <KpiCard
            label="Paused"
            value={mappings.filter((m) => m.status === "paused").length}
            trend="Needs attention"
            positive={false}
            icon="⏸"
          />
          <KpiCard
            label="Messages Routed"
            value="42"
            trend="↑ +11.0% today"
            icon="💬"
          />
        </div>

        <div className="filter-bar">
          <div className="search-wrap">
            <span className="search-icon">🔍</span>
            <input
              className="search-input"
              placeholder="Search number or group…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          {["all", "active", "paused"].map((s) => (
            <button
              key={s}
              className={`chip${statusFilter === s ? " chip--active" : ""}`}
              onClick={() => {
                setStatusFilter(s);
                setPage(1);
              }}
            >
              {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        <div className="table-wrap">
          {loading ? (
            <div className="table-empty">Loading mappings…</div>
          ) : (
            <>
              <div className="table-container">
                <table className="mapping-table">
                  <thead>
                    <tr>
                      <th>WhatsApp Number</th>
                      <th>Type</th>
                      <th>Mapped To</th>
                      <th>Sender Rule</th>
                      <th>Mapped By</th>
                      <th>Status</th>
                      <th>Last Routed</th>
                      <th>Routing</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="table-empty">
                          No mappings found
                        </td>
                      </tr>
                    ) : (
                      paged.map((m, i) => (
                        <tr key={m.id}>
                          <td>
                            <div className="number-cell">
                              <div
                                className="avatar"
                                style={{
                                  background:
                                    AVATAR_COLORS[i % AVATAR_COLORS.length],
                                }}
                              >
                                {(m.number || "??")
                                  .replace(/\D/g, "")
                                  .slice(-2) || "??"}
                              </div>
                              <div>
                                <div className="number-cell__name">
                                  {m.number || (
                                    <span style={{ color: "#aaa" }}>—</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`sender-rule-tag sender-rule-tag--${m.mappingType?.toLowerCase()}`}
                            >
                              {m.mappingType || "—"}
                            </span>
                          </td>
                          <td>
                            {m.mappingType === "DIRECT" ? (
                              <span className="room-link">
                                @{m.matrixUserId || "—"}
                              </span>
                            ) : (
                              <span className="room-link">
                                #{m.roomName || getGroupName(m.room) || "—"}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="sender-rule-tag">
                              {m.senderRule}
                            </span>
                          </td>
                          <td>{m.mappedBy}</td>
                          <td>
                            <StatusBadge status={m.status} />
                          </td>
                          <td className="last-routed">{m.lastRouted}</td>
                          <td>
                            <Toggle
                              on={m.status === "active"}
                              onChange={() => handleToggle(m.id)}
                            />
                          </td>
                          <td>
                            <RowMenu
                              status={m.status}
                              onEdit={() => {
                                setEditData({
                                  ...m,
                                  groupId:
                                    m.mappingType === "GROUP" ? m.room : "",
                                });
                                setShowDrawer(true);
                              }}
                              onLogs={() =>
                                showToast("📋 Opening routing logs…")
                              }
                              onToggle={() => handleToggle(m.id)}
                              onDelete={() => handleDelete(m.id)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {filtered.length > 0 && (
                <div className="pagination">
                  <span className="pagination__info">
                    Showing{" "}
                    {Math.min((page - 1) * pageSize + 1, filtered.length)}–
                    {Math.min(page * pageSize, filtered.length)} of{" "}
                    {filtered.length} mappings
                  </span>
                  <div className="pagination__btns">
                    <button
                      className="pagination__btn"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      ‹
                    </button>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`pagination__btn${page === pageNum ? " pagination__btn--active" : ""}`}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      className="pagination__btn"
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page === totalPages}
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showDrawer && (
        <AddMappingDrawer
          onClose={() => {
            setShowDrawer(false);
            setEditData(null);
          }}
          onSave={handleSave}
          editData={editData}
          saving={saving}
          groups={groups}
          users={users}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
